import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as NotificationsActions from '../notifications/notifications.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { TranslateService } from '@ngx-translate/core';
import * as InvitationsActions from './invitations.actions';
import { InvitationsService } from 'src/app/services/invitations.service';
import * as SiteActions from 'src/app/store/site/site.actions';


@Injectable()
export class InvitationsEffects {

    getInvitationsList$ = createEffect(() => this.actions$.pipe(
        ofType(InvitationsActions.getInvitationsList),
        switchMap((action) => this.invitationsService.getInvitationsList(action.pending, action.typeInvitations)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return InvitationsActions.getInvitationsListSuccess({ invitationsList: resp.Data });
                    }
                    return InvitationsActions.getInvitationsListFailed({ errorMessage: this.translate.instant('ERROR') });
                }),
                catchError(error => of(InvitationsActions.getInvitationsListFailed({ errorMessage: error.message })))
            )
        )
    ));

    shareDevice$ = createEffect(() => this.actions$.pipe(
        ofType(InvitationsActions.invitationsShareDevice),
        withLatestFrom(this.store.select('device', 'currentDevice')),
        switchMap(([action, device]) => this.invitationsService.invitationsShareDevice(
            action.email, action.firstName, action.lastName, device.Id, action.remotesMax
        ).pipe(
            map(resp => {
                if (resp.Success) {
                    return InvitationsActions.invitationsShareDeviceSuccess({ message: this.translate.instant('SHARE_DEVICE_SUCCESS') });
                } else {
                    if (resp.ErrorMessage.indexOf('user role not allowed')) {
                        return InvitationsActions.invitationsShareDeviceFailed({ errorMessage: this.translate.instant('SHARE_DEVICE_FAILED_ONLY_USER_ONE') });
                    }
                    return InvitationsActions.invitationsShareDeviceFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                }
            }),
            catchError(error => of(InvitationsActions.invitationsShareDeviceFailed({ errorMessage: error.message })))
        ))
    ));

    shareThGroup$ = createEffect(() => this.actions$.pipe(
        ofType(InvitationsActions.invitationsThGroupShare),
        switchMap((action) => this.invitationsService.invitationsThGroupShare(
            action.email, action.firstName, action.lastName, action.groupId,
        ).pipe(
            map(resp => {
                if (resp.Success) {
                    return InvitationsActions.invitationsThGroupShareSuccess({ message: this.translate.instant('SHARE_THGROUP_SUCCESS') });
                } else {
                    if (resp.ErrorMessage.indexOf('user role not allowed')) {
                        return InvitationsActions.invitationsThGroupShareFailed({ errorMessage: this.translate.instant('SHARE_DEVICE_FAILED_ONLY_USER_ONE') });
                    }
                    return InvitationsActions.invitationsThGroupShareFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                }
            }),
            catchError(error => of(InvitationsActions.invitationsThGroupShareFailed({ errorMessage: error.message })))
        ))
    ));

    invitationsAccept$ = createEffect(() => this.actions$.pipe(
        ofType(InvitationsActions.invitationsAccept),
        switchMap((action) => this.invitationsService.invitationsAccept(
            action.token
        ).pipe(
            map(resp => {
                if (resp.Success) {
                    return InvitationsActions.invitationsAcceptSuccess({ message: this.translate.instant('INVITATION_ACCEPT_SUCCESS') });
                } else {
                    return InvitationsActions.invitationsAcceptFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                }
            }),
            catchError(error => of(InvitationsActions.invitationsAcceptFailed({ errorMessage: error.message })))
        ))
    ));

    invitationsReject$ = createEffect(() => this.actions$.pipe(
        ofType(InvitationsActions.invitationsReject),
        switchMap((action) => this.invitationsService.invitationsReject(
            action.token
        ).pipe(
            map(resp => {
                if (resp.Success) {
                    return InvitationsActions.invitationsRejectSuccess({ message: this.translate.instant('INVITATION_REJECT_SUCCESS') });
                } else {
                    return InvitationsActions.invitationsRejectFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
                }
            }),
            catchError(error => of(InvitationsActions.invitationsRejectFailed({ errorMessage: error.message })))
        ))
    ));

    invitationsAcceptSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(InvitationsActions.invitationsAcceptSuccess),
        switchMap(() => [
            InvitationsActions.getInvitationsList({ pending: true }),
            SiteActions.getSites({}),
            SiteActions.getSitesOnlineDevices({}),
        ])
    ));

    invitationsRejectSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(InvitationsActions.invitationsRejectSuccess),
        switchMap(() => [
            InvitationsActions.getInvitationsList({ pending: true }),
        ])
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            InvitationsActions.invitationsShareDeviceSuccess,
            InvitationsActions.invitationsAcceptSuccess,
            InvitationsActions.invitationsRejectSuccess,
            InvitationsActions.invitationsThGroupShareSuccess,
        ),
        tap((action) => {
            const notification = new Notification("INVITATIONS", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            InvitationsActions.getInvitationsListFailed,
            InvitationsActions.invitationsShareDeviceFailed,
            InvitationsActions.invitationsAcceptFailed,
            InvitationsActions.invitationsRejectFailed,
            InvitationsActions.invitationsThGroupShareFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("INVITATIONS", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private invitationsService: InvitationsService,
        private translate: TranslateService,
        private store: Store<AppState>,
    ) { }
}

